import * as tutorApplicantConstants from './constants';
import getCookieOrMeta from '../../storage/getCookieOrMeta';
import getFieldValue from '../elements/getFieldValue';
import getForm from '../elements/getForm';
import { getTutorSummaryNote, getCustomFormFields } from './getMessageValue';
import { getQueryParam } from '../../tracking/params';
import submitForm from './submitForm';
import { validateCreateTutor } from '../validation/submitValidation';
import trackFormWizardSubmissionEvents from './trackFormWizardSubmissionEvents';

//

function getFormUrl() {
  return `https://${process.env.API_HOST}/vtwa/v3/tutor_applicants`;
}

function vttaPostApplicationURL() {
  return `https://${process.env.API_HOST}/supply/sourcing/v1/applicants`;
}

/**
 * Creates the tutor summary note field with all the tutor information
 * @param {Element} form - The form to query field from
 */
function getTutorNotes(form) {
  const tutorSummaryNote = getTutorSummaryNote(form);
  if (!tutorSummaryNote) return;

  const additionalFields = {
    referer: getCookieOrMeta('referer') || '',
    visitor_id: getCookieOrMeta('visitor-id')
  };

  const additionalFieldsString = Object.entries(additionalFields)
    .filter(([_, v]) => !!v)
    .map((entry) => `${entry[0]}: ${entry[1]}`)
    .join(' | ');

  const finalNote = additionalFieldsString
    ? tutorSummaryNote + ' | ' + additionalFieldsString
    : tutorSummaryNote;

  return finalNote.replace(/[\r\n]+/g, ' ').trim();
}

const getFirstName = (form) => (
  getFieldValue(form, 'first_name') ||
    (getFieldValue(form, 'full_name') || ' ').split(' ')[0]
);

const getLastName = (form) => (
  getFieldValue(form, 'last_name') ||
    (getFieldValue(form, 'full_name') || ' ').match(/ (.*)$/)[1]
);

function getFormData(form, {raw = false} = {}) {
  const response = {
    email: getFieldValue(form, 'email'),
    first_name: getFirstName(form).trim(),
    last_name: getLastName(form).trim(),
    phone_number: getFieldValue(form, 'phone_number'),
    tutor_summary_note: getTutorNotes(form),
    country_code: getFieldValue(form, 'country_code') || 'US',
    zip_code: getFieldValue(form, 'zip_code'),
    landing_page: getCookieOrMeta('landing_page'),
    utm_source: getQueryParam('utm_source'),
    utm_medium: getQueryParam('utm_medium'),
    utm_term: getQueryParam('utm_term'),
    utm_campaign: getQueryParam('utm_campaign'),
    subjects: mapSubjectIds(form, raw),
    flag_ids: mapTutorFlagIds(form, raw)
  };

  // Collect every field on the form and include them with our form data
  if (raw) {
    response.extra = getCustomFormFields(form, []);
  }

  return response;
}

function mapSubjectIds(form, raw) {
  const subjects = getFieldValue(form, 'areas_of_expertise');
  if (!subjects) return [];

  return subjects
    .split(',')
    .map((s) => raw ? s : tutorApplicantConstants.subjectIdsMap[(s && s.trim())])
    .filter((s) => s);
}

export function mapTutorFlagIds(form, raw) {
  // Support gettin a single highest education level (radio) or multiple selections (checkbox)
  const educations =
    getFieldValue(form, 'highest_education_level') ||
    getFieldValue(form, 'educational_degrees') ||
    '';
  const specializations = getFieldValue(form, 'teaching_certifications_specialization') || '';

  if (!educations && !specializations) return [];

  const flags = educations.split(',').concat(specializations.split(','));

  if (raw) {
    /*
      The map of tutor flags and its description/name reside in mex-components,
      and their names differ from VTWA, so we need to remap the flag_ids here
      so vt-tutor-applicant can have both values (description and id)
    */
    const initial = {};
    return flags.reduce((obj, s) => {
      return {
        ...obj,
        [s]: tutorApplicantConstants.educationIdsMap[(s && s.trim())],
      };
    }, initial);
  }

  return flags
    .map((s) => tutorApplicantConstants.educationIdsMap[(s && s.trim())])
    .filter((s) => s);
}
const saveInterviewUrl = (step, json) => {
  const interviewUrl = json.interview_url;
  // only save valid urls (in staging, interview_url is the literal string "null")
  if (interviewUrl && interviewUrl.match(/^(http:\/\/|https:\/\/|www\.)/)) {
    step.innerHTML = `<input type="hidden" name="interview_url" value="${interviewUrl}"/>`;
    return json;
  }
};

export default async function createTutor(step) {
  const form = getForm(step);
  const formData = getFormData(form);
  const formVersion = getFieldValue(form, 'leadform');
  const url = getFormUrl();

  validateCreateTutor(formData);

  const promise = submitForm(url, { formData: formData, formVersion: formVersion }, 'POST', {credentials: 'include'} ).then((json) => {
    saveInterviewUrl(step, json);

    // Submit the raw form data to vt-tutor-applicant.
    // This functionality is non-critical and should not affect the end user if it fails
    const vttaUrl = vttaPostApplicationURL();
    const rawFormData = getFormData(form, {raw: true});
    rawFormData.interview_url = json.interview_url;
    rawFormData.vtwa_id = json.id;
    rawFormData.vtwa_uuid = json.uuid;
    rawFormData.vtwa_resp = json;

    return submitForm(vttaUrl, {formData: rawFormData, formVersion: formVersion}, 'POST', {credentials: 'include'}).catch((error) => {
      if (!window.newrelic) {return;}

      const message = { 'message': `vt-tutor-applicant post fail endpoint: ${vttaUrl}` };
      window.newrelic.noticeError(error, { 'customAttributes': message });
    });
  });

  return trackFormWizardSubmissionEvents(
    promise, step, 'tutor_application_creation'
  ).catch((e) => {
    e.message = `Could not submit form.${e.rootCauses?.length ? ' ' + e.rootCauses[0] + '.' : ''}`;
    throw e;
  });
}
